// MyVouchers.js
import React, { useEffect, useMemo, useState } from 'react';
import {
  SafeAreaView,
  StyleSheet,
  View,
  Text,
  ScrollView,
  Image,
  TouchableOpacity,
  ActivityIndicator,
  RefreshControl,
  Alert,
  Linking,
} from 'react-native';
import { Ionicons } from '@expo/vector-icons';

const API_URL = 'https://collabbay.com/APIControllers/FetchCreatorVouchers.php';
const SESSION_URL = 'https://collabbay.com/APIControllers/GetSessionData.php';

const LOGO_URL = 'https://collabbay.com/CollabbayLogoDark.png';
const VOUCHER_BASE_URL = 'https://collabbay.com/Voucher/';
const CREATOR_CONTENT_BASE_URL = 'https://collabbay.com/Vouchers/';

const FALLBACK_IMAGE =
  'https://images.pexels.com/photos/1058277/pexels-photo-1058277.jpeg';

const CREATOR_EARNINGS_PER_AVAILABLE_VOUCHER = 5;

const STOCK_SORT_OPTIONS = [
  { key: 'latest', label: 'Latest' },
  { key: 'stock_high', label: 'Most stock' },
  { key: 'potential_high', label: 'Highest potential' },
  { key: 'sales_high', label: 'Most sales' },
  { key: 'brand_az', label: 'A–Z' },
];

const SALES_SORT_OPTIONS = [
  { key: 'latest', label: 'Newest' },
  { key: 'oldest', label: 'Oldest' },
  { key: 'amount_high', label: 'Highest amount' },
  { key: 'brand_az', label: 'Brand A–Z' },
];

const normaliseImageUri = (image) => {
  if (!image || !String(image).trim()) {
    return FALLBACK_IMAGE;
  }

  const trimmed = String(image).trim();

  if (trimmed.startsWith('http://') || trimmed.startsWith('https://')) {
    return trimmed;
  }

  if (trimmed.startsWith('/')) {
    return `https://collabbay.com${trimmed}`;
  }

  return `https://collabbay.com/${trimmed}`;
};

const formatNumber = (value) => {
  const number = Number(value || 0);

  return Number.isFinite(number)
    ? number.toLocaleString('en-GB')
    : '0';
};

const formatMoney = (value) => {
  const number = Number(value || 0);

  if (!Number.isFinite(number)) {
    return '£0';
  }

  return `£${number.toLocaleString('en-GB', {
    minimumFractionDigits: number % 1 === 0 ? 0 : 2,
    maximumFractionDigits: 2,
  })}`;
};

const toTimestamp = (value) => {
  if (!value) {
    return 0;
  }

  const date = new Date(value);

  return Number.isNaN(date.getTime()) ? 0 : date.getTime();
};

const formatDate = (value) => {
  if (!value) {
    return 'Not recorded';
  }

  const date = new Date(value);

  if (Number.isNaN(date.getTime())) {
    return String(value);
  }

  return date.toLocaleDateString('en-GB', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  });
};

const formatTime = (value) => {
  if (!value) {
    return '';
  }

  const date = new Date(value);

  if (Number.isNaN(date.getTime())) {
    return '';
  }

  return date.toLocaleTimeString('en-GB', {
    hour: '2-digit',
    minute: '2-digit',
  });
};

const formatDateTime = (value) => {
  if (!value) {
    return 'Not recorded';
  }

  const date = new Date(value);

  if (Number.isNaN(date.getTime())) {
    return String(value);
  }

  return date.toLocaleString('en-GB', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

const formatDealText = (voucher) => {
  const spend = Number(voucher?.OnSpend || 0);
  const discount = Number(voucher?.Discount || 0);

  if (spend > 0 && discount > 0) {
    return `${discount}% off when spending £${spend}`;
  }

  if (discount > 0) {
    return `${discount}% off voucher`;
  }

  return 'Creator voucher offer';
};

const getBrandName = (item) => {
  return item?.DisplayBrandName || item?.BrandName || 'Brand';
};

const getCurrentStock = (voucher) => {
  if (voucher?.CurrentStock !== undefined && voucher?.CurrentStock !== null) {
    return Math.max(Number(voucher.CurrentStock || 0), 0);
  }

  if (voucher?.Stock !== undefined && voucher?.Stock !== null) {
    return Math.max(Number(voucher.Stock || 0), 0);
  }

  if (voucher?.Remaining !== undefined && voucher?.Remaining !== null) {
    return Math.max(Number(voucher.Remaining || 0), 0);
  }

  return Math.max(Number(voucher?.StockAssigned || 0), 0);
};

const getPurchaseCount = (voucher) => {
  return Number(voucher?.PurchaseCount || voucher?.Redeemed || 0);
};

const getTotalAmountPaid = (voucher) => {
  return Number(voucher?.TotalAmountPaid || 0);
};

const getPurchaseDate = (purchase) => {
  return purchase?.DateTimePurchased || purchase?.CreatedAt || purchase?.Date;
};

const getVoucherUrl = (voucher) => {
  const collabID = voucher?.CollabID || '';
  return `${VOUCHER_BASE_URL}${encodeURIComponent(String(collabID))}`;
};

const getCreatorContentUrl = (promoter) => {
  const username = promoter || '';
  return `${CREATOR_CONTENT_BASE_URL}${encodeURIComponent(String(username))}`;
};

const getStockKey = (voucher, index) => {
  return `${voucher?.CollabID || 'voucher'}-${voucher?.BrandName || 'brand'}-${index}`;
};

const getPurchaseKey = (purchase, index) => {
  return `${purchase?.VoucherID || 'purchase'}-${purchase?.CollabID || 'collab'}-${index}`;
};

export default function MyVouchers({ navigation }) {
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  const [userData, setUserData] = useState({});
  const [vouchers, setVouchers] = useState([]);
  const [purchases, setPurchases] = useState([]);

  const [summary, setSummary] = useState({
    TotalDeals: 0,
    TotalCurrentStock: 0,
    TotalPurchases: 0,
    TotalAmountPaid: 0,
    PotentialCreatorEarnings: 0,
  });

  const [activeTab, setActiveTab] = useState('stock');
  const [stockSort, setStockSort] = useState('latest');
  const [salesSort, setSalesSort] = useState('latest');
  const [expandedStockKey, setExpandedStockKey] = useState(null);
  const [errorMessage, setErrorMessage] = useState('');

  const fetchUserData = async () => {
    try {
      const response = await fetch(SESSION_URL, {
        method: 'GET',
        credentials: 'include',
      });

      const data = await response.json();
      setUserData(data || {});
    } catch (error) {
      console.error('Fetch user data error:', error);
    }
  };

  const fetchCreatorVouchers = async () => {
    const response = await fetch(`${API_URL}?d=${Date.now()}`, {
      method: 'GET',
      credentials: 'include',
      headers: {
        Accept: 'application/json',
      },
    });

    const rawText = await response.text();

    let data;

    try {
      data = JSON.parse(rawText);
    } catch (error) {
      throw new Error(`Invalid voucher response: ${rawText.slice(0, 180)}`);
    }

    if (!response.ok || !data.success) {
      throw new Error(
        data.message || data.debug || 'Could not load voucher activity.'
      );
    }

    setVouchers(Array.isArray(data.vouchers) ? data.vouchers : []);

    setPurchases(
      Array.isArray(data.purchases)
        ? data.purchases
        : Array.isArray(data.ledger)
          ? data.ledger
          : []
    );

    setSummary(
      data.summary || {
        TotalDeals: 0,
        TotalCurrentStock: 0,
        TotalPurchases: 0,
        TotalAmountPaid: 0,
        PotentialCreatorEarnings: 0,
      }
    );

    setErrorMessage('');
  };

  const loadPage = async () => {
    try {
      await Promise.all([fetchUserData(), fetchCreatorVouchers()]);
    } catch (error) {
      console.error('Load creator vouchers error:', error);
      setErrorMessage(error.message || 'Could not load your voucher activity.');
    }
  };

  useEffect(() => {
    const initialise = async () => {
      setLoading(true);
      await loadPage();
      setLoading(false);
    };

    initialise();
  }, []);

  const onRefresh = async () => {
    setRefreshing(true);
    await loadPage();
    setRefreshing(false);
  };

  const overview = useMemo(() => {
    const currentStock = vouchers.reduce(
      (total, voucher) => total + getCurrentStock(voucher),
      0
    );

    const totalSalesAmount = purchases.reduce(
      (total, purchase) => total + Number(purchase.AmountPaid || 0),
      0
    );

    const purchaseCount = purchases.length || Number(summary.TotalPurchases || 0);

    const latestPurchase = [...purchases].sort(
      (a, b) => toTimestamp(getPurchaseDate(b)) - toTimestamp(getPurchaseDate(a))
    )[0];

    return {
      currentStock,
      potentialEarnings:
        currentStock * CREATOR_EARNINGS_PER_AVAILABLE_VOUCHER,
      activeBrands: vouchers.length,
      purchaseCount,
      totalSalesAmount,
      latestPurchaseDate: latestPurchase
        ? getPurchaseDate(latestPurchase)
        : null,
    };
  }, [vouchers, purchases, summary.TotalPurchases]);

  const sortedStock = useMemo(() => {
    const results = [...vouchers];

    results.sort((a, b) => {
      const aBrand = getBrandName(a).toLowerCase();
      const bBrand = getBrandName(b).toLowerCase();

      const aStock = getCurrentStock(a);
      const bStock = getCurrentStock(b);

      const aPotential = aStock * CREATOR_EARNINGS_PER_AVAILABLE_VOUCHER;
      const bPotential = bStock * CREATOR_EARNINGS_PER_AVAILABLE_VOUCHER;

      const aSales = getPurchaseCount(a);
      const bSales = getPurchaseCount(b);

      if (stockSort === 'stock_high') {
        return bStock - aStock;
      }

      if (stockSort === 'potential_high') {
        return bPotential - aPotential;
      }

      if (stockSort === 'sales_high') {
        return bSales - aSales;
      }

      if (stockSort === 'brand_az') {
        return aBrand.localeCompare(bBrand);
      }

      return (
        toTimestamp(b.LastUpdatedAt || b.FirstAssignedAt) -
        toTimestamp(a.LastUpdatedAt || a.FirstAssignedAt)
      );
    });

    return results;
  }, [vouchers, stockSort]);

  const sortedPurchases = useMemo(() => {
    const results = [...purchases];

    results.sort((a, b) => {
      const aBrand = getBrandName(a).toLowerCase();
      const bBrand = getBrandName(b).toLowerCase();

      if (salesSort === 'oldest') {
        return (
          toTimestamp(getPurchaseDate(a)) -
          toTimestamp(getPurchaseDate(b))
        );
      }

      if (salesSort === 'amount_high') {
        return Number(b.AmountPaid || 0) - Number(a.AmountPaid || 0);
      }

      if (salesSort === 'brand_az') {
        return aBrand.localeCompare(bBrand);
      }

      return (
        toTimestamp(getPurchaseDate(b)) -
        toTimestamp(getPurchaseDate(a))
      );
    });

    return results;
  }, [purchases, salesSort]);

  const handleFooterPress = (routeName, fallbackUrl = null) => {
    if (routeName && navigation?.navigate) {
      navigation.navigate(routeName);
      return;
    }

    if (fallbackUrl) {
      Linking.openURL(fallbackUrl);
    }
  };

  const openVoucherLink = async (voucher) => {
    const url = getVoucherUrl(voucher);

    try {
      const canOpen = await Linking.canOpenURL(url);

      if (canOpen) {
        await Linking.openURL(url);
        return;
      }

      Alert.alert('Voucher link', url);
    } catch (error) {
      Alert.alert('Voucher link', url);
    }
  };

  const openCreatorContentLink = async () => {
    const url = getCreatorContentUrl(userData?.Username);

    if (!userData?.Username) {
      Alert.alert('Content page', 'Your creator username could not be loaded yet.');
      return;
    }

    try {
      const canOpen = await Linking.canOpenURL(url);

      if (canOpen) {
        await Linking.openURL(url);
        return;
      }

      Alert.alert('Content page', url);
    } catch (error) {
      Alert.alert('Content page', url);
    }
  };

  const renderHeader = () => (
    <View style={styles.topHeader}>
      <View style={styles.headerIdentity}>
        <Image source={{ uri: LOGO_URL }} style={styles.logo} />

        <View style={{ flex: 1 }}>
          <Text style={styles.pageTitle}>My Vouchers</Text>
          <Text style={styles.pageSubtitle}>
            {userData?.Username
              ? `Hi, ${userData.Username}`
              : 'Creator voucher dashboard'}
          </Text>
        </View>
      </View>

      <TouchableOpacity
        style={styles.helpButton}
        onPress={() =>
          Alert.alert(
            'Your voucher dashboard',
            'Stock shows voucher batches currently available to share. Sales shows actual purchases made through your voucher links.'
          )
        }
        activeOpacity={0.85}
      >
        <Ionicons name="help-circle-outline" size={22} color="#111111" />
      </TouchableOpacity>
    </View>
  );

  const renderShareContentPage = () => {
    const shareUrl = getCreatorContentUrl(userData?.Username || '');

    return (
      <TouchableOpacity
        style={styles.shareContentCard}
        onPress={openCreatorContentLink}
        activeOpacity={0.9}
      >
        <View style={styles.shareContentCopy}>
          <Text style={styles.shareContentLabel}>SHARE MY CONTENT PAGE</Text>
          <Text style={styles.shareContentTitle}>Your live voucher page</Text>
          <Text style={styles.shareContentUrl} numberOfLines={1}>
            {userData?.Username ? shareUrl : 'Loading your shareable page...'}
          </Text>
        </View>

        <View style={styles.shareContentArrow}>
          <Ionicons name="arrow-forward-outline" size={17} color="#111111" />
        </View>
      </TouchableOpacity>
    );
  };

  const renderSummaryPill = (value, label) => (
    <View style={styles.summaryPill}>
      <Text style={styles.summaryPillNumber}>{value}</Text>
      <Text style={styles.summaryPillLabel}>{label}</Text>
    </View>
  );

  const renderHero = () => (
    <View style={styles.heroCard}>
      <Text style={styles.heroTitle}>
        Voucher Wallet<Text style={styles.heroDot}>.</Text>
      </Text>

      <Text style={styles.heroSubtitle}>
        Share live voucher links with your audience and track customer
        purchases in one place.
      </Text>

      <View style={styles.heroAmountRow}>
        <View>
          <Text style={styles.heroAmountLabel}>Total sales</Text>
          <Text style={styles.heroAmount}>
            {formatMoney(overview.totalSalesAmount)}
          </Text>
        </View>

        <View style={styles.heroStatusBadge}>
          <View style={styles.heroStatusDot} />
          <Text style={styles.heroStatusText}>Live</Text>
        </View>
      </View>

      <View style={styles.summaryRow}>
        {renderSummaryPill(formatNumber(overview.currentStock), 'Stock')}
        {renderSummaryPill(formatNumber(overview.purchaseCount), 'Sales')}
        {renderSummaryPill(formatMoney(overview.potentialEarnings), 'Potential')}
      </View>
    </View>
  );

  const renderSortChips = (options, activeSort, onSelect) => (
    <ScrollView
      horizontal
      showsHorizontalScrollIndicator={false}
      contentContainerStyle={styles.filterRow}
    >
      {options.map((option) => {
        const selected = option.key === activeSort;

        return (
          <TouchableOpacity
            key={option.key}
            style={[styles.filterChip, selected && styles.filterChipActive]}
            onPress={() => onSelect(option.key)}
            activeOpacity={0.85}
          >
            <Text
              style={[
                styles.filterChipText,
                selected && styles.filterChipTextActive,
              ]}
            >
              {option.label}
            </Text>
          </TouchableOpacity>
        );
      })}
    </ScrollView>
  );

  const renderControls = () => (
    <View style={styles.controlsCard}>
      <View style={styles.sectionHeaderRow}>
        <View>
          <Text style={styles.sectionTitle}>Voucher activity</Text>
          <Text style={styles.controlsSubtitle}>
            Switch between available stock and customer purchases.
          </Text>
        </View>
      </View>

      <View style={styles.tabRow}>
        <TouchableOpacity
          style={[styles.tabButton, activeTab === 'stock' && styles.tabButtonActive]}
          onPress={() => setActiveTab('stock')}
          activeOpacity={0.9}
        >
          <Ionicons
            name="cube-outline"
            size={17}
            color={activeTab === 'stock' ? '#111111' : '#666666'}
          />
          <Text
            style={[
              styles.tabButtonText,
              activeTab === 'stock' && styles.tabButtonTextActive,
            ]}
          >
            Stock
          </Text>
        </TouchableOpacity>

        <TouchableOpacity
          style={[styles.tabButton, activeTab === 'sales' && styles.tabButtonActive]}
          onPress={() => setActiveTab('sales')}
          activeOpacity={0.9}
        >
          <Ionicons
            name="receipt-outline"
            size={17}
            color={activeTab === 'sales' ? '#111111' : '#666666'}
          />
          <Text
            style={[
              styles.tabButtonText,
              activeTab === 'sales' && styles.tabButtonTextActive,
            ]}
          >
            Sales
          </Text>
        </TouchableOpacity>
      </View>

      <Text style={styles.sortLabel}>Sort by</Text>

      {activeTab === 'stock'
        ? renderSortChips(STOCK_SORT_OPTIONS, stockSort, setStockSort)
        : renderSortChips(SALES_SORT_OPTIONS, salesSort, setSalesSort)}
    </View>
  );

  const renderEmptyState = (icon, title, message) => (
    <View style={styles.emptyStateCard}>
      <View style={styles.emptyStateIcon}>
        <Ionicons name={icon} size={27} color="#111111" />
      </View>

      <Text style={styles.emptyTitle}>{title}</Text>
      <Text style={styles.emptyText}>{message}</Text>
    </View>
  );

  const renderRecentPurchase = (purchase, index) => (
    <View key={getPurchaseKey(purchase, index)} style={styles.recentSaleRow}>
      <View style={{ flex: 1 }}>
        <Text style={styles.recentSaleCustomer} numberOfLines={1}>
          {purchase.CustomerEmail || 'Customer purchase'}
        </Text>

        <Text style={styles.recentSaleDate}>
          {formatDateTime(getPurchaseDate(purchase))}
        </Text>
      </View>

      <Text style={styles.recentSaleAmount}>
        {formatMoney(purchase.AmountPaid)}
      </Text>
    </View>
  );

  const renderStockCard = (voucher, index) => {
    const key = getStockKey(voucher, index);
    const expanded = expandedStockKey === key;

    const brandName = getBrandName(voucher);
    const brandImage = normaliseImageUri(voucher.BrandProfileImage);
    const currentStock = getCurrentStock(voucher);
    const purchaseCount = getPurchaseCount(voucher);
    const totalAmountPaid = getTotalAmountPaid(voucher);
    const potentialValue =
      currentStock * CREATOR_EARNINGS_PER_AVAILABLE_VOUCHER;

    const updatedAt =
      voucher.LastPurchasedAt ||
      voucher.LastUpdatedAt ||
      voucher.FirstAssignedAt;

    const voucherPurchases = purchases
      .filter(
        (purchase) =>
          String(purchase.CollabID) === String(voucher.CollabID)
      )
      .sort(
        (a, b) =>
          toTimestamp(getPurchaseDate(b)) -
          toTimestamp(getPurchaseDate(a))
      )
      .slice(0, 3);

    const isLive = currentStock > 0;

    return (
      <View key={key} style={styles.voucherCard}>
        <TouchableOpacity
          style={styles.voucherCardHeader}
          onPress={() => setExpandedStockKey(expanded ? null : key)}
          activeOpacity={0.9}
        >
          <Image source={{ uri: brandImage }} style={styles.brandImage} />

          <View style={styles.brandContent}>
            <View style={styles.brandTitleRow}>
              <Text style={styles.brandName} numberOfLines={1}>
                {brandName}
              </Text>

              <View
                style={[
                  styles.voucherStatusBadge,
                  isLive ? styles.liveStatus : styles.usedStatus,
                ]}
              >
                <Text style={styles.voucherStatusText}>
                  {isLive ? 'Live' : 'Sold out'}
                </Text>
              </View>
            </View>

            <Text style={styles.brandOfferText} numberOfLines={1}>
              {formatDealText(voucher)}
            </Text>

            <Text style={styles.updatedText}>
              Updated {formatDate(updatedAt)}
            </Text>
          </View>

          <Ionicons
            name={expanded ? 'chevron-up' : 'chevron-down'}
            size={19}
            color="#666666"
          />
        </TouchableOpacity>

        <View style={styles.stockHighlightsRow}>
          <View style={styles.stockHighlight}>
            <Text style={styles.highlightValue}>
              {formatNumber(currentStock)}
            </Text>
            <Text style={styles.highlightLabel}>Available</Text>
          </View>

          <View style={styles.stockHighlightDivider} />

          <View style={styles.stockHighlight}>
            <Text style={styles.highlightValue}>
              {formatMoney(potentialValue)}
            </Text>
            <Text style={styles.highlightLabel}>Potential</Text>
          </View>

          <View style={styles.stockHighlightDivider} />

          <View style={styles.stockHighlight}>
            <Text style={styles.highlightValue}>
              {formatNumber(purchaseCount)}
            </Text>
            <Text style={styles.highlightLabel}>Sold</Text>
          </View>
        </View>

        <TouchableOpacity
          style={styles.quickVoucherLink}
          onPress={() => openVoucherLink(voucher)}
          activeOpacity={0.9}
        >
          <Text style={styles.quickVoucherLinkText}>Open shareable purchase link</Text>
          <Ionicons name="arrow-forward-outline" size={16} color="#111111" />
        </TouchableOpacity>

        {expanded ? (
          <View style={styles.expandedContent}>
            <View style={styles.divider} />

            <View style={styles.detailsSummaryRow}>
              <View style={styles.detailSummaryItem}>
                <Text style={styles.detailSummaryLabel}>Sales received</Text>
                <Text style={styles.detailSummaryValue}>
                  {formatMoney(totalAmountPaid)}
                </Text>
              </View>

              <View style={styles.detailSummaryItem}>
                <Text style={styles.detailSummaryLabel}>Created</Text>
                <Text style={styles.detailSummaryValue}>
                  {formatDate(voucher.FirstAssignedAt)}
                </Text>
              </View>
            </View>

            <View style={styles.voucherLinkPanel}>
              <Text style={styles.voucherLinkLabel}>Customer sales page</Text>
              <Text style={styles.voucherLinkUrl} numberOfLines={1}>
                {getVoucherUrl(voucher)}
              </Text>

              <TouchableOpacity
                style={styles.openLinkButton}
                onPress={() => openVoucherLink(voucher)}
                activeOpacity={0.9}
              >
                <Text style={styles.openLinkText}>Open voucher link</Text>
                <Ionicons name="open-outline" size={17} color="#111111" />
              </TouchableOpacity>
            </View>

            <View style={styles.recentSalesPanel}>
              <View style={styles.recentSalesHeader}>
                <Text style={styles.recentSalesTitle}>Recent sales</Text>
                <Text style={styles.recentSalesHint}>
                  {voucherPurchases.length > 0
                    ? `${voucherPurchases.length} shown`
                    : 'No sales yet'}
                </Text>
              </View>

              {voucherPurchases.length > 0 ? (
                voucherPurchases.map(renderRecentPurchase)
              ) : (
                <Text style={styles.noPurchaseText}>
                  Sales made through this voucher link will appear here.
                </Text>
              )}
            </View>
          </View>
        ) : null}
      </View>
    );
  };

  const renderStockTab = () => (
    <View>
      <View style={styles.sectionHeaderRow}>
        <Text style={styles.sectionTitle}>Current stock</Text>
        <Text style={styles.sectionHint}>
          {formatNumber(sortedStock.length)} brand
          {sortedStock.length === 1 ? '' : 's'}
        </Text>
      </View>

      {sortedStock.length === 0 ? (
        renderEmptyState(
          'ticket-outline',
          'No live voucher stock',
          'When a brand assigns vouchers to you, each voucher batch will appear here.'
        )
      ) : (
        sortedStock.map(renderStockCard)
      )}
    </View>
  );

  const renderPurchaseRow = (purchase, index) => {
    const brandName = getBrandName(purchase);
    const brandImage = normaliseImageUri(purchase.BrandProfileImage);
    const purchaseDate = getPurchaseDate(purchase);

    return (
      <View key={getPurchaseKey(purchase, index)} style={styles.invoiceRow}>
        <View style={styles.invoiceDateColumn}>
          <Text style={styles.invoiceDate}>
            {formatDate(purchaseDate)}
          </Text>
          <Text style={styles.invoiceTime}>
            {formatTime(purchaseDate)}
          </Text>
        </View>

        <Image source={{ uri: brandImage }} style={styles.invoiceImage} />

        <View style={styles.invoiceContent}>
          <Text style={styles.invoiceBrand} numberOfLines={1}>
            {brandName}
          </Text>

          <Text style={styles.invoiceCustomer} numberOfLines={1}>
            {purchase.CustomerEmail || 'Customer email unavailable'}
          </Text>

          <Text style={styles.invoiceReference} numberOfLines={1}>
            Passcode: {purchase.VoucherPasscode || '—'} · Voucher #{purchase.VoucherID || '—'}
          </Text>
        </View>

        <View style={styles.invoiceAmountColumn}>
          <Text style={styles.invoiceAmount}>
            {formatMoney(purchase.AmountPaid)}
          </Text>

          <View style={styles.paidStatus}>
            <View style={styles.paidDot} />
            <Text style={styles.paidText}>Paid</Text>
          </View>
        </View>
      </View>
    );
  };

  const renderSalesTab = () => (
    <View>
      <View style={styles.sectionHeaderRow}>
        <Text style={styles.sectionTitle}>Sales ledger</Text>
        <Text style={styles.sectionHint}>
          {formatNumber(sortedPurchases.length)} purchase
          {sortedPurchases.length === 1 ? '' : 's'}
        </Text>
      </View>

      {sortedPurchases.length === 0 ? (
        renderEmptyState(
          'receipt-outline',
          'No voucher sales yet',
          'Purchases made by customers through your voucher links will appear here.'
        )
      ) : (
        <View style={styles.invoiceCard}>
          <View style={styles.invoiceHeader}>
            <Text style={[styles.invoiceHeaderText, styles.invoiceDateHeader]}>
              Date
            </Text>
            <Text style={[styles.invoiceHeaderText, { flex: 1 }]}>
              Purchase
            </Text>
            <Text style={styles.invoiceHeaderText}>Amount</Text>
          </View>

          {sortedPurchases.map(renderPurchaseRow)}
        </View>
      )}
    </View>
  );

  if (loading) {
    return (
      <View style={styles.loadingContainer}>
        <ActivityIndicator size="large" color="black" />
        <Text style={styles.loadingText}>
          Loading your voucher dashboard...
        </Text>
      </View>
    );
  }

  return (
    <View style={styles.outerPage}>
      <View style={styles.yellowStrip} />

      <SafeAreaView style={styles.safeArea}>
        <View style={styles.container}>
          <ScrollView
            contentContainerStyle={styles.scrollViewContent}
            showsVerticalScrollIndicator={false}
            refreshControl={
              <RefreshControl refreshing={refreshing} onRefresh={onRefresh} />
            }
          >
            {renderHeader()}
            {renderShareContentPage()}

            {errorMessage ? (
              <View style={styles.errorCard}>
                <Text style={styles.errorTitle}>Could not load vouchers</Text>
                <Text style={styles.errorMessage}>{errorMessage}</Text>
              </View>
            ) : null}

            {renderHero()}
            {renderControls()}

            {activeTab === 'stock' ? renderStockTab() : renderSalesTab()}

            <TouchableOpacity
              style={styles.privacyLink}
              onPress={() =>
                Linking.openURL('https://collabbay.com/PrivacyPolicy.html')
              }
            >
              <Text style={styles.privacyText}>
                By using collabbay, you agree to our Privacy Policy.
              </Text>
            </TouchableOpacity>
          </ScrollView>

          <View style={styles.footer}>
            <TouchableOpacity
              style={styles.footerIcon}
              onPress={() => handleFooterPress('Homepage')}
            >
              <Ionicons name="home" size={24} color="black" />
            </TouchableOpacity>

            <TouchableOpacity
              style={styles.footerIcon}
              onPress={() => handleFooterPress('CollabCalendar')}
            >
              <Ionicons name="calendar" size={24} color="black" />
            </TouchableOpacity>

            <TouchableOpacity
              onPress={() => handleFooterPress('MyFeed')}
              style={styles.footerMainButton}
            >
              <Ionicons name="film-outline" size={22} color="black" />
            </TouchableOpacity>

            <TouchableOpacity
              style={styles.footerIcon}
              onPress={() => handleFooterPress('UserActivityFeed')}
            >
              <Ionicons name="chatbox-ellipses-outline" size={24} color="black" />
            </TouchableOpacity>

            <TouchableOpacity
              style={styles.footerIcon}
              onPress={() => handleFooterPress('UserRecapPage')}
            >
              <Ionicons name="person" size={24} color="black" />
            </TouchableOpacity>
          </View>
        </View>
      </SafeAreaView>
    </View>
  );
}

const styles = StyleSheet.create({
  outerPage: {
    flex: 1,
    backgroundColor: '#FFFFFF',
    alignItems: 'center',
  },
  yellowStrip: {
    height: 12,
    width: '100%',
    maxWidth: 600,
    backgroundColor: '#FDF05D',
  },
  safeArea: {
    flex: 1,
    width: '100%',
    maxWidth: 600,
    backgroundColor: '#FFFFFF',
  },
  container: {
    flex: 1,
    width: '100%',
    backgroundColor: '#FFFFFF',
    paddingHorizontal: 18,
  },
  scrollViewContent: {
    paddingBottom: 100,
    paddingTop: 10,
  },

  loadingContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    backgroundColor: '#FFFFFF',
  },
  loadingText: {
    marginTop: 12,
    fontSize: 15,
    color: '#555555',
    fontWeight: '400',
  },

  topHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingTop: 10,
    paddingBottom: 16,
  },
  headerIdentity: {
    flex: 1,
    flexDirection: 'row',
    alignItems: 'center',
  },
  logo: {
    width: 44,
    height: 44,
    borderRadius: 22,
    backgroundColor: '#111111',
    marginRight: 11,
  },
  pageTitle: {
    fontSize: 23,
    fontWeight: '600',
    color: '#111111',
    letterSpacing: -0.35,
  },
  pageSubtitle: {
    marginTop: 2,
    fontSize: 13,
    color: '#666666',
    fontWeight: '400',
  },
  helpButton: {
    width: 40,
    height: 40,
    borderRadius: 20,
    backgroundColor: '#F7F7F7',
    justifyContent: 'center',
    alignItems: 'center',
  },

  shareContentCard: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: '#F7F7F7',
    borderRadius: 22,
    paddingVertical: 14,
    paddingHorizontal: 15,
    marginBottom: 16,
  },
  shareContentCopy: {
    flex: 1,
    paddingRight: 12,
  },
  shareContentLabel: {
    fontSize: 10,
    color: '#666666',
    fontWeight: '600',
    letterSpacing: 0.9,
    marginBottom: 5,
  },
  shareContentTitle: {
    fontSize: 16,
    color: '#111111',
    fontWeight: '600',
    marginBottom: 4,
  },
  shareContentUrl: {
    fontSize: 12,
    color: '#666666',
    fontWeight: '400',
  },
  shareContentArrow: {
    width: 36,
    height: 36,
    borderRadius: 18,
    backgroundColor: '#FDF05D',
    alignItems: 'center',
    justifyContent: 'center',
  },

  errorCard: {
    backgroundColor: '#FFF5F5',
    borderWidth: 1,
    borderColor: '#F2CCCC',
    borderRadius: 20,
    padding: 14,
    marginBottom: 16,
  },
  errorTitle: {
    fontSize: 14,
    fontWeight: '600',
    color: '#9D2020',
  },
  errorMessage: {
    marginTop: 4,
    fontSize: 13,
    color: '#9D2020',
    lineHeight: 18,
    fontWeight: '400',
  },

  heroCard: {
    backgroundColor: '#FDF05D',
    borderRadius: 28,
    padding: 22,
    marginBottom: 16,
  },
  heroTitle: {
    fontSize: 29,
    fontWeight: '700',
    color: '#111111',
    marginBottom: 6,
    letterSpacing: -0.5,
  },
  heroDot: {
    color: '#111111',
  },
  heroSubtitle: {
    fontSize: 14,
    lineHeight: 20,
    color: '#333333',
    fontWeight: '400',
    marginBottom: 18,
  },
  heroAmountRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'flex-end',
    marginBottom: 17,
  },
  heroAmountLabel: {
    fontSize: 12,
    color: '#333333',
    fontWeight: '500',
    marginBottom: 4,
  },
  heroAmount: {
    fontSize: 32,
    lineHeight: 36,
    color: '#111111',
    fontWeight: '700',
    letterSpacing: -0.6,
  },
  heroStatusBadge: {
    backgroundColor: 'rgba(255,255,255,0.70)',
    borderRadius: 999,
    paddingHorizontal: 11,
    paddingVertical: 7,
    flexDirection: 'row',
    alignItems: 'center',
  },
  heroStatusDot: {
    width: 7,
    height: 7,
    borderRadius: 3.5,
    backgroundColor: '#39925B',
    marginRight: 6,
  },
  heroStatusText: {
    color: '#222222',
    fontSize: 12,
    fontWeight: '500',
  },
  summaryRow: {
    flexDirection: 'row',
    gap: 10,
  },
  summaryPill: {
    flex: 1,
    backgroundColor: 'rgba(255,255,255,0.72)',
    borderRadius: 20,
    paddingVertical: 12,
    paddingHorizontal: 8,
    alignItems: 'center',
  },
  summaryPillNumber: {
    fontSize: 18,
    fontWeight: '600',
    color: '#111111',
    marginBottom: 3,
  },
  summaryPillLabel: {
    fontSize: 12,
    color: '#333333',
    fontWeight: '400',
  },

  controlsCard: {
    backgroundColor: '#F7F7F7',
    borderRadius: 28,
    padding: 18,
    marginBottom: 18,
  },
  sectionHeaderRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 11,
  },
  sectionTitle: {
    fontSize: 20,
    fontWeight: '600',
    color: '#111111',
    letterSpacing: -0.2,
  },
  sectionHint: {
    fontSize: 12,
    color: '#666666',
    fontWeight: '500',
  },
  controlsSubtitle: {
    fontSize: 14,
    color: '#666666',
    lineHeight: 20,
    fontWeight: '400',
    marginTop: 4,
  },
  tabRow: {
    flexDirection: 'row',
    marginTop: 15,
    marginBottom: 15,
    gap: 9,
  },
  tabButton: {
    flex: 1,
    minHeight: 46,
    backgroundColor: '#FFFFFF',
    borderRadius: 18,
    alignItems: 'center',
    justifyContent: 'center',
    flexDirection: 'row',
    gap: 7,
    borderWidth: 1,
    borderColor: '#FFFFFF',
  },
  tabButtonActive: {
    backgroundColor: '#FDF05D',
    borderColor: '#E7DC54',
  },
  tabButtonText: {
    fontSize: 14,
    color: '#666666',
    fontWeight: '500',
  },
  tabButtonTextActive: {
    color: '#111111',
    fontWeight: '600',
  },
  sortLabel: {
    fontSize: 12,
    color: '#666666',
    fontWeight: '500',
    marginBottom: 9,
  },
  filterRow: {
    paddingRight: 6,
  },
  filterChip: {
    paddingVertical: 9,
    paddingHorizontal: 13,
    borderRadius: 999,
    backgroundColor: '#FFFFFF',
    marginRight: 8,
  },
  filterChipActive: {
    backgroundColor: '#111111',
  },
  filterChipText: {
    fontSize: 12,
    fontWeight: '500',
    color: '#111111',
  },
  filterChipTextActive: {
    color: '#FFFFFF',
  },

  emptyStateCard: {
    backgroundColor: '#F7F7F7',
    borderRadius: 24,
    paddingVertical: 28,
    paddingHorizontal: 18,
    alignItems: 'center',
    marginBottom: 14,
  },
  emptyStateIcon: {
    width: 52,
    height: 52,
    borderRadius: 26,
    backgroundColor: '#FDF05D',
    justifyContent: 'center',
    alignItems: 'center',
  },
  emptyTitle: {
    marginTop: 11,
    fontSize: 17,
    fontWeight: '600',
    color: '#111111',
  },
  emptyText: {
    marginTop: 6,
    fontSize: 14,
    textAlign: 'center',
    color: '#666666',
    lineHeight: 19,
    fontWeight: '400',
  },

  voucherCard: {
    backgroundColor: '#FFFFFF',
    borderRadius: 28,
    padding: 18,
    marginBottom: 14,
    borderWidth: 1,
    borderColor: '#EEEEEE',
    shadowColor: '#000000',
    shadowOpacity: 0.05,
    shadowRadius: 16,
    shadowOffset: { width: 0, height: 6 },
    elevation: 2,
  },
  voucherCardHeader: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  brandImage: {
    width: 62,
    height: 62,
    borderRadius: 31,
    backgroundColor: '#F1F1F1',
    marginRight: 12,
  },
  brandContent: {
    flex: 1,
    paddingRight: 8,
  },
  brandTitleRow: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  brandName: {
    flex: 1,
    fontSize: 17,
    fontWeight: '600',
    color: '#111111',
    letterSpacing: -0.15,
  },
  brandOfferText: {
    marginTop: 4,
    fontSize: 13,
    color: '#555555',
    fontWeight: '400',
  },
  updatedText: {
    marginTop: 4,
    fontSize: 12,
    color: '#888888',
    fontWeight: '400',
  },
  voucherStatusBadge: {
    borderRadius: 999,
    paddingHorizontal: 9,
    paddingVertical: 5,
    marginLeft: 7,
  },
  liveStatus: {
    backgroundColor: '#DDF5E3',
  },
  usedStatus: {
    backgroundColor: '#EFEFEF',
  },
  voucherStatusText: {
    fontSize: 11,
    color: '#111111',
    fontWeight: '500',
  },

  stockHighlightsRow: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: '#FAFAFA',
    borderRadius: 18,
    paddingVertical: 12,
    paddingHorizontal: 8,
    marginTop: 15,
  },
  stockHighlight: {
    flex: 1,
    alignItems: 'center',
  },
  stockHighlightDivider: {
    width: 1,
    height: 29,
    backgroundColor: '#E6E6E6',
  },
  highlightValue: {
    fontSize: 17,
    color: '#111111',
    fontWeight: '600',
    marginBottom: 3,
  },
  highlightLabel: {
    fontSize: 11,
    color: '#666666',
    fontWeight: '400',
  },
  quickVoucherLink: {
    marginTop: 12,
    backgroundColor: '#FDF05D',
    borderRadius: 999,
    paddingVertical: 11,
    paddingHorizontal: 14,
    alignItems: 'center',
    justifyContent: 'space-between',
    flexDirection: 'row',
  },
  quickVoucherLinkText: {
    fontSize: 13,
    color: '#111111',
    fontWeight: '600',
  },

  expandedContent: {
    marginTop: 1,
  },
  divider: {
    height: 1,
    backgroundColor: '#F0F0F0',
    marginTop: 15,
    marginBottom: 15,
  },
  detailsSummaryRow: {
    flexDirection: 'row',
    gap: 10,
    marginBottom: 13,
  },
  detailSummaryItem: {
    flex: 1,
    backgroundColor: '#F7F7F7',
    borderRadius: 17,
    padding: 12,
  },
  detailSummaryLabel: {
    fontSize: 11,
    color: '#666666',
    fontWeight: '400',
    marginBottom: 5,
  },
  detailSummaryValue: {
    fontSize: 15,
    color: '#111111',
    fontWeight: '600',
  },
  voucherLinkPanel: {
    backgroundColor: '#FFFFFF',
    borderWidth: 1,
    borderColor: '#EEEEEE',
    borderRadius: 18,
    padding: 13,
    marginBottom: 13,
  },
  voucherLinkLabel: {
    fontSize: 12,
    color: '#666666',
    fontWeight: '500',
    marginBottom: 5,
  },
  voucherLinkUrl: {
    fontSize: 12,
    color: '#333333',
    fontWeight: '400',
    marginBottom: 12,
  },
  openLinkButton: {
    backgroundColor: '#FDF05D',
    borderRadius: 999,
    paddingVertical: 11,
    paddingHorizontal: 14,
    alignItems: 'center',
    justifyContent: 'center',
    flexDirection: 'row',
    gap: 7,
  },
  openLinkText: {
    fontSize: 14,
    color: '#111111',
    fontWeight: '600',
  },

  recentSalesPanel: {
    backgroundColor: '#F7F7F7',
    borderRadius: 18,
    padding: 12,
  },
  recentSalesHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    marginBottom: 4,
  },
  recentSalesTitle: {
    fontSize: 13,
    color: '#111111',
    fontWeight: '600',
  },
  recentSalesHint: {
    fontSize: 12,
    color: '#666666',
    fontWeight: '400',
  },
  recentSaleRow: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingVertical: 9,
    borderTopWidth: 1,
    borderTopColor: '#E8E8E8',
  },
  recentSaleCustomer: {
    fontSize: 12,
    color: '#222222',
    fontWeight: '500',
  },
  recentSaleDate: {
    marginTop: 3,
    fontSize: 11,
    color: '#777777',
    fontWeight: '400',
  },
  recentSaleAmount: {
    fontSize: 13,
    color: '#111111',
    fontWeight: '600',
    marginLeft: 10,
  },
  noPurchaseText: {
    paddingTop: 7,
    fontSize: 12,
    color: '#666666',
    lineHeight: 18,
    fontWeight: '400',
  },

  invoiceCard: {
    backgroundColor: '#FFFFFF',
    borderRadius: 28,
    marginBottom: 14,
    borderWidth: 1,
    borderColor: '#EEEEEE',
    shadowColor: '#000000',
    shadowOpacity: 0.05,
    shadowRadius: 16,
    shadowOffset: { width: 0, height: 6 },
    elevation: 2,
    overflow: 'hidden',
  },
  invoiceHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: '#F7F7F7',
    paddingVertical: 12,
    paddingHorizontal: 15,
    borderBottomWidth: 1,
    borderBottomColor: '#EEEEEE',
  },
  invoiceHeaderText: {
    fontSize: 11,
    color: '#666666',
    fontWeight: '500',
    textTransform: 'uppercase',
    letterSpacing: 0.3,
  },
  invoiceDateHeader: {
    width: 86,
  },
  invoiceRow: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingVertical: 13,
    paddingHorizontal: 15,
    borderBottomWidth: 1,
    borderBottomColor: '#F0F0F0',
  },
  invoiceDateColumn: {
    width: 86,
    paddingRight: 6,
  },
  invoiceDate: {
    fontSize: 12,
    color: '#111111',
    fontWeight: '500',
  },
  invoiceTime: {
    marginTop: 3,
    fontSize: 11,
    color: '#777777',
    fontWeight: '400',
  },
  invoiceImage: {
    width: 45,
    height: 45,
    borderRadius: 22.5,
    backgroundColor: '#F1F1F1',
    marginRight: 10,
  },
  invoiceContent: {
    flex: 1,
    paddingRight: 8,
  },
  invoiceBrand: {
    fontSize: 13,
    color: '#111111',
    fontWeight: '600',
  },
  invoiceCustomer: {
    marginTop: 3,
    fontSize: 12,
    color: '#555555',
    fontWeight: '400',
  },
  invoiceReference: {
    marginTop: 3,
    fontSize: 11,
    color: '#888888',
    fontWeight: '400',
  },
  invoiceAmountColumn: {
    alignItems: 'flex-end',
  },
  invoiceAmount: {
    fontSize: 15,
    color: '#111111',
    fontWeight: '600',
  },
  paidStatus: {
    marginTop: 5,
    backgroundColor: '#DDF5E3',
    borderRadius: 999,
    paddingHorizontal: 8,
    paddingVertical: 4,
    flexDirection: 'row',
    alignItems: 'center',
  },
  paidDot: {
    width: 6,
    height: 6,
    borderRadius: 3,
    backgroundColor: '#39925B',
    marginRight: 5,
  },
  paidText: {
    fontSize: 10,
    color: '#111111',
    fontWeight: '500',
  },

  privacyLink: {
    marginTop: 24,
    marginBottom: 8,
  },
  privacyText: {
    fontSize: 11,
    color: '#111111',
    textDecorationLine: 'underline',
    textAlign: 'center',
  },

  footer: {
    flexDirection: 'row',
    justifyContent: 'space-around',
    position: 'sticky',
    bottom: 0,
    backgroundColor: 'white',
    left: 0,
    right: 0,
    paddingVertical: 10,
    borderTopWidth: 1,
    borderTopColor: '#f0f0f0',
  },
  footerIcon: {
    alignItems: 'center',
    justifyContent: 'center',
    width: 42,
    height: 42,
  },
  footerMainButton: {
    backgroundColor: '#FDF05D',
    padding: 10,
    borderRadius: 20,
  },
});